import { useState, useCallback, useRef } from 'react';
import { supabase } from '../components/Data/DataProvider';
import { buildSimilarityUrl, normalizeExpert } from '../utils/expertUtils';

/**
 * Hook to handle expert search functionality
 * Manages: Similarity API calls, search state, query management
 */
export function useExpertSearch({ backfillMetadata }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchMode, setIsSearchMode] = useState(false);
  const fetchSeqRef = useRef(0);

  const fetchSimilarityResults = useCallback(async (query) => {
    const url = buildSimilarityUrl(query);

    const resp = await fetch(url, {
      headers: {
        apikey: process.env.REACT_APP_SUPABASE_ANON_KEY,
        'Content-Type': 'application/json',
      },
    });

    if (!resp.ok) {
      throw new Error(`Similarity API failed (${resp.status})`);
    }

    const json = await resp.json();
    const ids = (json.data?.results ?? [])
      .map(r => Number(r.model_id))
      .filter(Boolean);

    return ids;
  }, []);

  const handleSearch = useCallback(async (query, { onStart, onSuccess, onError } = {}) => {
    const trimmedQuery = (query || searchQuery || '').trim();
    if (!trimmedQuery) return;

    const seq = ++fetchSeqRef.current;
    setIsSearchMode(true);

    if (onStart) onStart();

    try {
      const ids = await fetchSimilarityResults(trimmedQuery);

      if (seq !== fetchSeqRef.current) return;

      const { data: experts } = ids.length
        ? await supabase.from('experts').select('*').in('id', ids)
        : { data: [] };

      const byId = new Map((experts || []).map((e) => [Number(e.id), e]));
      const ordered = ids
        .map((id) => byId.get(id))
        .filter(Boolean)
        .map((e) => normalizeExpert(e, { isVip: false }));

      const firstPaint = ordered.map(({ sortWeight, ...rest }) => rest);

      if (seq !== fetchSeqRef.current) return;

      if (onSuccess) {
        const enriched = await backfillMetadata(firstPaint);
        onSuccess(enriched);
      }
    } catch (err) {
      if (seq === fetchSeqRef.current && onError) {
        onError(err.message);
      }
    }
  }, [searchQuery, fetchSimilarityResults, backfillMetadata]);

  const resetSearch = useCallback(() => {
    setSearchQuery('');
    setIsSearchMode(false);
  }, []);

  return {
    searchQuery,
    setSearchQuery,
    isSearchMode,
    handleSearch,
    resetSearch,
    fetchSimilarityResults,
    fetchSeqRef,
  };
}
